import type {
  DashboardCapabilityContext,
  DashboardLayoutPreset,
  DashboardRoleBucket,
  DashboardWidgetConfig,
  DashboardWidgetDataKey,
} from "./types";
import { ORG_ADMIN_ROLE_CODES } from "./roles";

const DASHBOARD_LAYOUTS: Record<DashboardRoleBucket, DashboardLayoutPreset> = {
  SUPPORT: {
    bucket: "SUPPORT",
    widgets: [
      {
        id: "BUSINESS_STATS",
        access: { permissionsAny: ["company:read"] },
        dataKeys: ["BUSINESS_SUMMARY"],
      },
      { id: "BUSINESS_QUICK_ACTIONS", access: { rolesAny: ["SUPER_ADMIN"] } },
      {
        id: "BUSINESS_SYSTEM_OVERVIEW",
        access: { permissionsAny: ["company:read"] },
        dataKeys: ["BUSINESS_SUMMARY"],
      },
    ],
  },
  ORG_ADMIN: {
    bucket: "ORG_ADMIN",
    widgets: [
      {
        id: "BUSINESS_STATS",
        access: { permissionsAny: ["company:read"] },
        dataKeys: ["BUSINESS_SUMMARY"],
      },
      {
        id: "BUSINESS_QUICK_ACTIONS",
        access: {
          rolesAny: [...ORG_ADMIN_ROLE_CODES],
          permissionsAny: ["company:update", "user:create", "user:read"],
        },
      },
      {
        id: "BUSINESS_SYSTEM_OVERVIEW",
        access: { permissionsAny: ["company:read"] },
        dataKeys: ["BUSINESS_SUMMARY"],
      },
    ],
  },
  MANAGER: {
    bucket: "MANAGER",
    widgets: [{ id: "MANAGER_RECIPE_OVERVIEW", access: { permissionsAny: ["recipe:read"] } }],
  },
  CHEF: {
    bucket: "CHEF",
    widgets: [{ id: "CHEF_EXPERIENCE", access: { permissionsAny: ["recipe:read"] } }],
  },
};

function canSeeWidget(
  widget: DashboardWidgetConfig,
  context: DashboardCapabilityContext,
): boolean {
  if (!widget.access) return true;

  const roleAllowed = widget.access.rolesAny?.length
    ? widget.access.rolesAny.some((role) => context.hasRole(role))
    : true;

  const permissionAllowed = widget.access.permissionsAny?.length
    ? widget.access.permissionsAny.some((permission) => context.hasPermission(permission))
    : true;

  return roleAllowed && permissionAllowed;
}

export function resolveDashboardWidgets(
  bucket: DashboardRoleBucket,
  context: DashboardCapabilityContext,
) {
  return DASHBOARD_LAYOUTS[bucket].widgets.filter((widget) => canSeeWidget(widget, context));
}

export function hasWidgetDataKey(
  widgets: DashboardWidgetConfig[],
  dataKey: DashboardWidgetDataKey,
) {
  return widgets.some((widget) => widget.dataKeys?.includes(dataKey));
}
